import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchDates,
  fetchDeclarationsByDate,
  fetchIndustries,
  removeDeclarations,
  sendDeclarations,
  generateDeclarations,
  checkSentDrafts,
  updateDeclarationIndustry,
  updateAgentTitle,
  refreshAllDeclarations
} from '../api/declarationService';
import {
  fetchMergeConflicts
} from '../api/agentService';

import {updateAgentEmail} from '../api/agentService';
import GlobalRefreshButton from './GlobalRefreshButton';

function TableDeclarations() {
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [declarations, setDeclarations] = useState([]);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState('');
  const [validationMessage, setValidationMessage] = useState('');
  const [editingEmails, setEditingEmails] = useState({});
  const [emailErrors, setEmailErrors] = useState({});
  const [industries, setIndustries] = useState([]);
  const [showSidebar, setShowSidebar] = useState(true);
  const [isGeneratingEmails, setIsGeneratingEmails] = useState(false);
  const [isSendingEmails, setIsSendingEmails] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [activeSummary, setActiveSummary] = useState(null);
  const [validationMessageOpacity, setValidationMessageOpacity] = useState(1);
  const [messageOpacity, setMessageOpacity] = useState(1);
  const [fadeOutTimer, setFadeOutTimer] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [declarationToDelete, setDeclarationToDelete] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [emailToConfirm, setEmailToConfirm] = useState(null);
  const [showEmailConfirmModal, setShowEmailConfirmModal] = useState(false);

  const [showSendConfirmModal, setShowSendConfirmModal] = useState(false);

  const [conflictedAgentIds, setConflictedAgentIds] = useState(new Set());

  const titleOptions = [
    'Herr',
    'Herr Dr.',
    'Herr Prof. Dr.',
    'Frau',
    'Frau Dr.',
    'Frau Prof. Dr.'
  ];

  const navigate = useNavigate();

  useEffect(() => {
    if (message) {
      setIsNotificationVisible(true);
      const timer = setTimeout(() => {
        setIsNotificationVisible(false);
        setTimeout(() => {
          setMessage('');
        }, 500);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    let timer;
    if (validationMessage) {
      setValidationMessageOpacity(1);
      timer = setTimeout(() => {
        const fadeEffect = setInterval(() => {
          setValidationMessageOpacity((prev) => {
            if (prev <= 0.1) {
              clearInterval(fadeEffect);
              setValidationMessage('');
              return 0;
            }
            return prev - 0.1;
          });
        }, 50);
        setTimeout(() => clearInterval(fadeEffect), 600);
      }, 7000);
      setFadeOutTimer(timer);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [validationMessage]);

  useEffect(() => {
    fetchDates()
      .then((dateArray) => {
        setDates(dateArray);
        const todayStr = new Date().toISOString().slice(0, 10);
        if (dateArray.includes(todayStr)) {
          setSelectedDate(todayStr);
        } else if (dateArray.length > 0) {
          setSelectedDate(dateArray[0]);
        }
      })
      .catch((err) => console.error('Error fetching dates:', err));
  }, []);

  useEffect(() => {
    if (selectedDate) {
      setMessage('');
      setValidationMessage('');
      setIsPageLoading(true);

      fetchDeclarationsByDate(selectedDate)
        .then((data) => {
          setDeclarations(data);
          setSelected([]);
          setValidationErrors({});

          setTimeout(() => {
            setIsPageLoading(false);
            const tableElement = document.querySelector('.table-declarations-container');
            if (tableElement) {
              tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        })
        .catch((err) => {
          console.error('Error fetching declarations:', err);
          setIsPageLoading(false);
        });
    } else {
      setDeclarations([]);
      setSelected([]);
    }
  }, [selectedDate]);

  useEffect(() => {
    const handleClickOutside = () => {
      setShowTooltip(false);
      setTimeout(() => {
        setActiveSummary(null);
      }, );
    };
  
    if (activeSummary) {
      window.addEventListener('click', handleClickOutside);
    }
  
    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
  }, [activeSummary]);

  useEffect(() => {
    fetchIndustries()
      .then(setIndustries)
      .catch((err) => console.error('Error fetching industries:', err));
  }, []);

  useEffect(() => {
    if (selected.length > 0) {
      validateSelectedDeclarations();
    } else {
      setValidationErrors({});
      setValidationMessage('');
    }
  }, [selected, declarations]);

  useEffect(() => {
    fetchMergeConflicts()
      .then(data => {
        if (!data.conflicts) return;
        const conflictSet = new Set();
        data.conflicts.forEach(({ agent1, agent2 }) => {
          conflictSet.add(agent1.agent_id);
          conflictSet.add(agent2.agent_id);
        });
        setConflictedAgentIds(conflictSet);
      })
      .catch(err => {
        console.error('Error fetching merge conflicts:', err);
      });
  }, []);

  const hasRowErrors = (declarationId) => {
    const decl = declarations.find((d) => d.declaration_id === declarationId);
    if (!decl) return false;
    return (
      !decl.agent_title ||
      !decl.agent_email ||
      !decl.industry_id ||
      decl.industry_id === 0
    );
  };

  const handleRefreshStart = () => {
    setIsRefreshing(true);
    setMessage('Refreshing all declarations, please wait...');
  };
  const handleRefreshComplete = (result) => {
    setIsRefreshing(false);
    setMessage(
      `Refreshed all declarations. Found ${result.sent_declarations.length} newly sent emails and ${result.deleted_drafts.length} deleted drafts.`
    );
    fetchDates()
      .then((dateArray) => {
        setDates(dateArray);
        if (selectedDate && !dateArray.includes(selectedDate)) {
          if (dateArray.length > 0) {
            setSelectedDate(dateArray[0]);
          } else {
            setSelectedDate(null);
            setDeclarations([]);
          }
        } else if (selectedDate) {
          return fetchDeclarationsByDate(selectedDate).then((data) => {
            setDeclarations(data);
            setSelected([]);
          });
        }
      })
      .catch((err) => {
        console.error('Error fetching dates after refresh:', err);
        setMessage(`Error refreshing date list: ${err.message}`);
      });
  };

  const PageOverlay = ({ isVisible }) => {
    if (!isVisible) return null;
    return (
      <div className="overlay">
        <div className="spinner"></div>
        <p className="overlay-text">Refreshing All Declarations...</p>
      </div>
    );
  };

  const SummaryTooltip = ({ summary, position }) => {
    if (!summary) return null;
    const tooltipWidth = 300;
    const tooltipHeight = 200;
    const padding = 10;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = position.top + window.scrollY + padding;
    let left = position.left + window.scrollX - tooltipWidth / 2;

    if (top + tooltipHeight > viewportHeight + window.scrollY) {
      top = position.top + window.scrollY - tooltipHeight - padding;
    }
    if (left + tooltipWidth > viewportWidth + window.scrollX) {
      left = viewportWidth + window.scrollX - tooltipWidth - padding;
    }
    if (left < 0) {
      left = padding;
    }

    return (
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          top,
          left: left - 20,
          width: `${tooltipWidth}px`,
          padding: '8px 12px',
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          fontSize: '14px',
          maxHeight: `${tooltipHeight}px`,
          overflowY: 'auto',
          whiteSpace: 'normal',
          transition: 'opacity 0.2s ease-in-out'
        }}
      >
        <div
          dangerouslySetInnerHTML={{
            __html: summary
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&quot;/g, '"')
              .replace(/&amp;/g, '&')
          }}
        />
      </div>
    );
  };

  const validateSelectedDeclarations = () => {
    const errors = {};
    let hasErrors = false;

    const missingTitle = [];
    const missingEmail = [];
    const missingIndustry = [];

    selected.forEach((id) => {
      const decl = declarations.find((d) => d.declaration_id === id);
      if (!decl) return;

      if (!decl.agent_title) {
        missingTitle.push({
          aktenzeichen: decl.aktenzeichen,
          agent_name: decl.agent_name,
          company_name: decl.company_name
        });
        hasErrors = true;
      }
      if (!decl.agent_email) {
        missingEmail.push({
          aktenzeichen: decl.aktenzeichen,
          agent_name: decl.agent_name,
          company_name: decl.company_name
        });
        hasErrors = true;
      }
      if (!decl.industry_id || decl.industry_id === 0) {
        missingIndustry.push({
          aktenzeichen: decl.aktenzeichen,
          agent_name: decl.agent_name,
          company_name: decl.company_name
        });
        hasErrors = true;
      }
    });

    if (missingTitle.length > 0) errors.title = missingTitle;
    if (missingEmail.length > 0) errors.email = missingEmail;
    if (missingIndustry.length > 0) errors.industry = missingIndustry;

    setValidationErrors(errors);
    return !hasErrors;
  };

  const handleCheckboxChange = (e, declarationId) => {
    if (e.target.checked) {
      setSelected((prev) => [...prev, declarationId]);
    } else {
      setSelected((prev) => prev.filter((id) => id !== declarationId));
    }
  };

  const handleTitleChange = (agentId, newTitle) => {
    if (!newTitle) return;
    updateAgentTitle(agentId, newTitle)
      .then((updatedAgent) => {
        setDeclarations((prev) =>
          prev.map((d) =>
            d.agent_id === agentId ? { ...d, agent_title: updatedAgent.title } : d
          )
        );
      })
      .catch((err) => {
        console.error('Error updating agent title:', err);
        setMessage(`Error updating agent title: ${err.message}`);
      });
  };

  const handleIndustryChange = (declarationId, newIndustryId) => {
    const industryId = parseInt(newIndustryId, 10);
    if (industryId === 0) return;

    updateDeclarationIndustry(declarationId, industryId)
      .then((updatedDecl) => {
        setDeclarations((prev) =>
          prev.map((d) =>
            d.declaration_id === declarationId ? { ...d, ...updatedDecl } : d
          )
        );
      })
      .catch((err) => {
        console.error('Error updating industry:', err);
        setMessage(`Error updating industry: ${err.message}`);
      });
  };

  const handleRemoveDeclaration = (declarationId, companyName) => {
    setDeclarationToDelete({ declarationId, companyName });
    setModalOpen(true);
  };

  const confirmDelete = () => {
    if (!declarationToDelete) return;
    setMessage(`Deleting declaration for "${declarationToDelete.companyName}"...`);

    removeDeclarations([declarationToDelete.declarationId])
      .then((data) => {
        setMessage(
          data.message ||
            `Declaration for "${declarationToDelete.companyName}" deleted successfully.`
        );
        if (selectedDate) {
          fetchDeclarationsByDate(selectedDate)
            .then((data) => {
              setDeclarations(data);
            })
            .catch((err) => {
              console.error('Error refreshing declarations:', err);
              setMessage(`Error refreshing declarations: ${err.message}`);
            });
        }
        setModalOpen(false);
        setDeclarationToDelete(null);
      })
      .catch((error) => {
        console.error('Error removing declaration:', error);
        setMessage(
          `Error deleting declaration: ${error.message || 'Unknown error'}`
        );
      });
  };

  const handleConfirmEmailUpdate = () => {
    const { agentId, declarationId, newEmail } = emailToConfirm;
    updateAgentEmail(agentId, newEmail)
      .then((updatedAgent) => {
        setDeclarations((prev) =>
          prev.map((d) =>
            d.declaration_id === declarationId
              ? { ...d, agent_email: updatedAgent.email }
              : d
          )
        );
        setEditingEmails((prev) => {
          const copy = { ...prev };
          delete copy[declarationId];
          return copy;
        });
      })
      .catch((err) => {
        console.error('Error updating agent email:', err);
        setMessage(`Error updating email: ${err.message}`);
      })
      .finally(() => {
        setEmailToConfirm(null);
        setShowEmailConfirmModal(false);
      });
  };

  const scrollToTop = () => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleGenerateEmails = () => {
    if (selected.length === 0) return;

    scrollToTop();

    if (!validateSelectedDeclarations()) {
      setValidationMessage('Address the displayed issues for the selected declarations.');
      return;
    }
    setIsGeneratingEmails(true);
    setMessage('Generating email drafts, please wait...');
    generateDeclarations(selected)
      .then((data) => {
        setMessage(data.message || '');
        if (data.redirectUrl) {
          window.open(data.redirectUrl, '_blank');
        }
        setSelected([]);
      })
      .catch((error) => {
        console.error('Error generating emails:', error);
        setMessage('');
        if (error.responseData && error.responseData.error_message) {
          setValidationMessage(
            `Failed to generate email drafts. ${error.responseData.error_message}\n\nPlease refresh the page and try again.`
          );
          setValidationMessageOpacity(1);
          clearTimeout(fadeOutTimer);
        } else {
          const errMsg = error.message || String(error);
          if (
            errMsg.includes('already generated') ||
            errMsg.includes('already has a draft')
          ) {
            setValidationMessage(
              'Draft was already generated for one or more selected declarations. Please refresh and try again.'
            );
          } else {
            setValidationMessage(
              `Failed to generate email drafts: ${errMsg}. Please refresh and try again.`
            );
          }
        }
      })
      .finally(() => setIsGeneratingEmails(false));
  };

  const handleSendEmailsDirectly = () => {
    if (selected.length === 0) return;
    if (!validateSelectedDeclarations()) {
      setValidationMessage('Please fill in all required fields for selected declarations.');
      return;
    }
    setShowSendConfirmModal(true);
  };
  
  const confirmSendEmailsNow = () => {
    setIsSendingEmails(true);
    setMessage('Sending emails directly, please wait...');
  
    sendDeclarations(selected)
      .then((data) => {
        setMessage(data.message || '');
        if (data.redirectUrl) {
          window.open(data.redirectUrl, '_blank');
        }
        setSelected([]);
        if (selectedDate) {
          fetchDeclarationsByDate(selectedDate)
            .then((data) => {
              setDeclarations(data);
            })
            .catch(console.error);
        }
      })
      .catch((error) => {
        console.error('Error sending emails:', error);
        setMessage('');
        if (error.responseData && error.responseData.error_message) {
          setValidationMessage(
            `Failed to send emails. ${error.responseData.error_message}\n\nPlease refresh the page and try again.`
          );
          setValidationMessageOpacity(1);
          clearTimeout(fadeOutTimer);
        } else {
          const errMsg = error.message || String(error);
          if (errMsg.includes('already sent') || errMsg.includes('already emailed')) {
            setValidationMessage(
              'An email was already sent for one or more selected declarations. Please refresh and try again.'
            );
          } else {
            setValidationMessage(
              `Failed to send emails: ${errMsg}. Please refresh and try again.`
            );
          }
        }
      })
      .finally(() => {
        setIsSendingEmails(false);
        setShowSendConfirmModal(false);
      });
  };

  const handleDeselectAll = () => {
    setSelected([]);
    setValidationErrors({});
    setValidationMessage('');
  };

  const handleToggleSummary = (e, summary) => {
    e.stopPropagation();
  
    const rect = e.currentTarget.getBoundingClientRect();
    const tooltipHeight = 200;
    const tooltipWidth = 300;
    const paddingBelow = 8;
    const paddingAbove = 4;
  
    const spaceBelow = window.innerHeight - rect.bottom;
    const showAbove = spaceBelow < tooltipHeight + paddingBelow;
  
    let top = showAbove
      ? rect.top - tooltipHeight - paddingAbove
      : rect.bottom + paddingBelow;
  
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
  
    if (left + tooltipWidth > window.innerWidth) {
      left = window.innerWidth - tooltipWidth - paddingBelow;
    }
    if (left < paddingBelow) {
      left = paddingBelow;
    }
  
    if (activeSummary && activeSummary.text === summary) {
      setActiveSummary(null);
      setTimeout(() => setActiveSummary(null), 200);
    } else {
      setActiveSummary({
        text: summary,
        position: { top, left }
      });
      setShowTooltip(true);
    }
  };
  

  const renderValidationErrors = () => {
    if (Object.keys(validationErrors).length === 0) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 60,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '90%',
          maxWidth: '800px',
          zIndex: 1000,
          padding: '1rem',
          backgroundColor: '#fff1f0',
          border: '1px solid #ffa39e',
          borderRadius: '4px',
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
        }}
      >
        <h4 style={{ marginTop: 0, color: '#cf1322' }}>
          Please address the following issues:
        </h4>
        <ul style={{ marginBottom: 0 }}>
          {validationErrors.title && (
            <li>
              <strong>Missing agent titles:</strong>
              <ul>
                {validationErrors.title.map((item, idx) => (
                  <li key={`title-${idx}`}>
                    <strong>{item.aktenzeichen}</strong> - {item.company_name} (Agent:{' '}
                    {item.agent_name})
                  </li>
                ))}
              </ul>
            </li>
          )}
          {validationErrors.email && (
            <li>
              <strong>Missing agent emails:</strong>
              <ul>
                {validationErrors.email.map((item, idx) => (
                  <li key={`email-${idx}`}>
                    <strong>{item.aktenzeichen}</strong> - {item.company_name} (Agent:{' '}
                    {item.agent_name})
                  </li>
                ))}
              </ul>
            </li>
          )}
          {validationErrors.industry && (
            <li>
              <strong>Missing industries:</strong>
              <ul>
                {validationErrors.industry.map((item, idx) => (
                  <li key={`industry-${idx}`}>
                    <strong>{item.aktenzeichen}</strong> - {item.company_name} (Agent:{' '}
                    {item.agent_name})
                  </li>
                ))}
              </ul>
            </li>
          )}
        </ul>
      </div>
    );
  };

  const containerStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    position: 'relative'
  };

  const mainContentStyle = {
    flexGrow: 1
  };
  const loadingMessageStyle = {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: '#fff8e1',
    border: '1px solid #ffe57f',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center'
  };
  const deleteIconStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '4px',
    backgroundColor: '#ff4d4f',
    color: 'white',
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
    transition: 'all 0.2s ease',
    padding: '4px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  };

  return (
    <div>
      <PageOverlay isVisible={isRefreshing} />

      {activeSummary && (
        <SummaryTooltip summary={activeSummary.text} position={activeSummary.position} />
      )}

      <h2>Insolvency Declarations</h2>

      {message && (
        <div className="message"
          style={{
            opacity: isNotificationVisible ? 1 : 0,
          }}
        >
          {message}
        </div>
      )}

      {validationMessage && (
        <div
          style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: '#fff2e8',
            border: '1px solid #ffbb96',
            borderRadius: '4px',
            opacity: validationMessageOpacity,
            transition: 'opacity 0.5s ease-out',
            whiteSpace: 'pre-line'
          }}
        >
          {validationMessage}
        </div>
      )}

      {renderValidationErrors()}

      {(isGeneratingEmails || isSendingEmails) && (
        <div style={loadingMessageStyle}>
          <div>
            {isGeneratingEmails
              ? 'Generating email drafts...'
              : 'Sending emails directly...'}{' '}
            This may take a moment.
          </div>
          <div
            style={{
              display: 'inline-block',
              marginLeft: '10px',
              verticalAlign: 'middle'
            }}
          >
            <div
              style={{
                width: '20px',
                height: '20px',
                border: '3px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '50%',
                borderTop: '3px solid #3498db',
                animation: 'spin 1s linear infinite'
              }}
            ></div>
          </div>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '1rem'
        }}
      >
        <button
          className="button-hide"
          onClick={() => setShowSidebar(!showSidebar)}
        >
          {showSidebar ? (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              Hide Navigation
            </>
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16m-7 6h7"
                />
              </svg>
              Show Navigation
            </>
          )}
        </button>

        <button
          className="button-bin"
          onClick={() => navigate('/bin')}
          disabled={isGeneratingEmails || isSendingEmails}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
            />
          </svg>
          View Bin
        </button>
      </div>

      <div style={containerStyle}>
        {showSidebar && (
          <div className="sidebar">
            <h4 className="sidebar-heading">
              Dates with open Declarations
            </h4>
            {dates.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#777' }}>
                No open declarations found.
              </p>
            ) : (
              <ul>
                {dates.map((dateStr) => (
                  <li
                    key={dateStr}
                    className={`date-item ${selectedDate === dateStr ? 'active' : ''}`}
                    onClick={() => setSelectedDate(dateStr)}
                  >
                    {dateStr}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div style={mainContentStyle}>
          {selectedDate ? (
            <>
              <div
                style={{
                  background: '#ffffff',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  padding: '1.5rem',
                  marginBottom: '1.5rem',
                  position: 'relative'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '1rem',
                    padding: '0 0 1rem 0',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                >
                  <p style={{ margin: 0, fontSize: '1rem' }}>
                    Showing declarations for <strong>{selectedDate}</strong>
                  </p>
                </div>

                {isPageLoading && (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '1rem',
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      zIndex: 10,
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <div
                      style={{
                        width: '30px',
                        height: '30px',
                        border: '3px solid rgba(0, 0, 0, 0.1)',
                        borderRadius: '50%',
                        borderTop: '3px solid #1890ff',
                        animation: 'spin 1s linear infinite'
                      }}
                    ></div>
                  </div>
                )}

                {declarations.length === 0 ? (
                  <div
                    style={{
                      padding: '2rem',
                      textAlign: 'center',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px',
                      marginTop: '1rem'
                    }}
                  >
                    No declarations for this date.
                  </div>
                ) : (
                  <>
                    <div style={{ overflowX: 'auto' }}>
                      <table>
                        <thead>
                          <tr
                            style={{
                              backgroundColor: '#fafafa',
                              borderBottom: '2px solid #f0f0f0'
                            }}
                          >
                            <th
                              style={{
                                padding: '12px 16px',
                                textAlign: 'center',
                                fontWeight: '600'
                              }}
                            >
                              Select
                            </th>
                            <th
                              style={{
                                padding: '12px 16px',
                                textAlign: 'left',
                                fontWeight: '600'
                              }}
                            >
                              Aktenzeichen
                            </th>
                            <th
                              style={{
                                padding: '12px 16px',
                                textAlign: 'left',
                                fontWeight: '600'
                              }}
                            >
                              Publication Date
                            </th>
                            <th
                              style={{
                                padding: '12px 16px',
                                textAlign: 'left',
                                fontWeight: '600'
                              }}
                            >
                              Company Name
                            </th>
                            <th
                              style={{
                                padding: '12px 16px',
                                textAlign: 'left',
                                fontWeight: '600'
                              }}
                            >
                              Agent Title
                            </th>
                            <th
                              style={{
                                padding: '12px 16px',
                                textAlign: 'left',
                                fontWeight: '600'
                              }}
                            >
                              Agent Name
                            </th>
                            <th
                              style={{
                                padding: '12px 16px',
                                textAlign: 'left',
                                fontWeight: '600'
                              }}
                            >
                              Agent Email
                            </th>
                            <th
                              style={{
                                padding: '12px 16px',
                                textAlign: 'left',
                                fontWeight: '600'
                              }}
                            >
                              Industry
                            </th>
                            <th
                              style={{
                                padding: '12px 16px',
                                textAlign: 'center',
                                fontWeight: '600'
                              }}
                            >
                              Delete
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {declarations.map((decl, idx) => (
                            <tr
                              key={decl.declaration_id}
                              style={{
                                borderBottom: '1px solid #f0f0f0',
                                backgroundColor:
                                  selected.includes(decl.declaration_id) &&
                                  hasRowErrors(decl.declaration_id)
                                    ? '#fff1f0'
                                    : idx % 2 === 0
                                    ? '#ffffff'
                                    : '#fafafa',
                                opacity: decl.agent_blacklist ? 0.65 : 1,
                                color: decl.agent_blacklist ? '#999999' : 'inherit'
                              }}
                            >
                              <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  onChange={(e) => handleCheckboxChange(e, decl.declaration_id)}
                                  checked={selected.includes(decl.declaration_id)}
                                  disabled={
                                    isGeneratingEmails || isSendingEmails || decl.agent_blacklist
                                  }
                                />
                              </td>
                              <td style={{ padding: '12px 16px', position: 'relative' }}>
                                {decl.aktenzeichen}
                                {decl.summary && (
                                  <span
                                    style={{
                                      position: 'absolute',
                                      right: '8px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: '18px',
                                      height: '18px',
                                      borderRadius: '50%',
                                      backgroundColor: '#1890ff',
                                      color: 'white',
                                      cursor: 'pointer'
                                    }}
                                    onClick={(e) => handleToggleSummary(e, decl.summary)}
                                  >
                                    i
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                {decl.publication_date}
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                <span>{decl.company_name}</span>
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                <select
                                  value={decl.agent_title || ''}
                                  onChange={(e) => handleTitleChange(decl.agent_id, e.target.value)}
                                  disabled={
                                    isGeneratingEmails ||
                                    isSendingEmails ||
                                    decl.agent_blacklist
                                  }
                                  style={{
                                    padding: '6px',
                                    borderColor:
                                      selected.includes(decl.declaration_id) &&
                                      !decl.agent_title
                                        ? '#ff4d4f'
                                        : '#d9d9d9',
                                    borderRadius: '4px',
                                    width: '100%'
                                  }}
                                >
                                  <option value="" disabled={!!decl.agent_title}>
                                    -- Select Title --
                                  </option>
                                  {titleOptions.map((title) => (
                                    <option key={title} value={title}>
                                      {title}
                                    </option>
                                  ))}
                                </select>
                              </td>


                              {/* Agent Name Cell */}
                              <td style={{ padding: '12px 16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                  <span>{decl.agent_name}</span>
                                   {/* If this agent is in the conflict set, display 'CONFLICT' in red */}
                                   {conflictedAgentIds.has(decl.agent_id) && (
                                    <span 
                                      style={{ marginLeft: '8px', color: 'red'}}
                                      title="This agent may have a merge conflict"
                                    >
                                      (Conflict)
                                    </span>
                                  )}
                                  {decl.agent_blacklist && (
                                    <span
                                      title="This agent is blacklisted"
                                      style={{
                                        marginLeft: '8px',
                                        display: 'inline-block',
                                        color: '#cf1322',
                                        flexShrink: 0
                                      }}
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.5"
                                        stroke="currentColor"
                                        width="16"
                                        height="16"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                                        />
                                      </svg>
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                {decl.agent_email ? (
                                  decl.agent_email
                                ) : (
                                  <>
                                    <input
                                      type="email"
                                      placeholder="Type agent email"
                                      value={editingEmails[decl.declaration_id] || ''}
                                      onChange={(e) => {
                                        setEditingEmails((prev) => ({
                                          ...prev,
                                          [decl.declaration_id]: e.target.value
                                        }));
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          e.target.blur();
                                        }
                                      }}
                                      disabled={
                                        isGeneratingEmails ||
                                        isSendingEmails ||
                                        decl.agent_blacklist
                                      }
                                      style={{
                                        padding: '6px',
                                        width: '100%',
                                        borderColor:
                                          selected.includes(decl.declaration_id) &&
                                          !decl.agent_email
                                            ? '#ff4d4f'
                                            : '#d9d9d9',
                                        borderRadius: '4px'
                                      }}
                                      onBlur={() => {
                                        const newEmail =
                                          editingEmails[decl.declaration_id] || '';
                                        const trimmedEmail = newEmail.trim();
                                        if (!trimmedEmail) {
                                          setEmailErrors((prev) => {
                                            const copy = { ...prev };
                                            delete copy[decl.declaration_id];
                                            return copy;
                                          });
                                          return;
                                        }

                                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                        if (!emailRegex.test(trimmedEmail)) {
                                          setEmailErrors((prev) => ({
                                            ...prev,
                                            [decl.declaration_id]: 'Invalid email format.'
                                          }));
                                          return;
                                        } else {
                                          setEmailErrors((prev) => {
                                            const copy = { ...prev };
                                            delete copy[decl.declaration_id];
                                            return copy;
                                          });
                                        }
                                        
                                        setEmailToConfirm({
                                          declarationId: decl.declaration_id,
                                          agentId: decl.agent_id,
                                          agentName: decl.agent_name,
                                          newEmail: trimmedEmail,
                                        });
                                        setShowEmailConfirmModal(true);
                                      }}
                                    />
                                    {emailErrors[decl.declaration_id] && (
                                      <div
                                        style={{
                                          color: '#ff4d4f',
                                          fontSize: '0.8em',
                                          marginTop: '4px'
                                        }}
                                      >
                                        {emailErrors[decl.declaration_id]}
                                      </div>
                                    )}
                                  </>
                                )}
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                <select
                                  value={decl.industry_id || 0}
                                  onChange={(e) =>
                                    handleIndustryChange(decl.declaration_id, e.target.value)
                                  }
                                  disabled={
                                    isGeneratingEmails ||
                                    isSendingEmails ||
                                    decl.agent_blacklist
                                  }
                                  style={{
                                    padding: '6px',
                                    width: '100%',
                                    borderColor:
                                      selected.includes(decl.declaration_id) &&
                                      (!decl.industry_id || decl.industry_id === 0)
                                        ? '#ff4d4f'
                                        : '#d9d9d9',
                                    borderRadius: '4px'
                                  }}
                                >
                                  <option
                                    value="0"
                                    disabled={
                                      !!(decl.industry_id && decl.industry_id !== 0)
                                    }
                                  >
                                    -- Select Industry --
                                  </option>
                                  {industries.map((ind) => (
                                    <option key={ind.industry_id} value={ind.industry_id}>
                                      {ind.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                <button
                                  style={deleteIconStyle}
                                  onClick={() =>
                                    handleRemoveDeclaration(
                                      decl.declaration_id,
                                      decl.company_name
                                    )
                                  }
                                  disabled={isGeneratingEmails || isSendingEmails}
                                  title="Delete declaration"
                                  aria-label="Delete declaration"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1.5"
                                    stroke="currentColor"
                                    width="16"
                                    height="16"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                    />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {modalOpen && (
                      <div className="popup-overlay">
                        <div className="popup-content">
                          <h3 style={{color: '#cf1322'}}>
                            Confirm Deletion
                          </h3>
                          <p>
                            Are you sure you want to delete the declaration for{' '}
                            <strong>{declarationToDelete?.companyName}</strong>?
                          </p>
                          <div className="popup-actions">
                            <button
                              className="button-delete"
                              onClick={confirmDelete}
                            >
                              Yes, Delete
                            </button>
                            <button
                              className="button-cancel"
                              onClick={() => setModalOpen(false)}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div
                      className="fixed-button-container"
                      style={{
                        marginTop: '1.5rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                    >
                      <GlobalRefreshButton
                        onRefreshStart={handleRefreshStart}
                        onRefreshComplete={handleRefreshComplete}
                        className="button-small"
                        disabled={isGeneratingEmails || isSendingEmails || isRefreshing}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: isRefreshing ? '#f5f5f5' : '#f0f2ff',
                          color: isRefreshing ? '#bfbfbf' : '#1890ff',
                          border: `1px solid ${
                            isRefreshing ? '#e0e0e0' : '#d6e4ff'
                          }`,
                          borderRadius: '4px',
                          cursor:
                            isGeneratingEmails || isSendingEmails || isRefreshing
                              ? 'not-allowed'
                              : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      />

                      {selected.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '10px' }}>
                          <button
                            className="button-small"
                            onClick={handleGenerateEmails}
                            disabled={isGeneratingEmails || isSendingEmails}
                            style={{
                              padding: '8px 16px',
                              background: isGeneratingEmails ? '#d3adf7' : '#6F42C1',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor:
                                isGeneratingEmails || isSendingEmails
                                  ? 'not-allowed'
                                  : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                              />
                            </svg>
                            Generate Email Drafts ({selected.length})
                          </button>

                          <button
                            className="button-small"
                            onClick={handleSendEmailsDirectly}
                            disabled={isGeneratingEmails || isSendingEmails}
                            style={{
                              padding: '8px 16px',
                              background: isSendingEmails ? '#95de64' : '#4CAF50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor:
                                isGeneratingEmails || isSendingEmails
                                  ? 'not-allowed'
                                  : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              />
                            </svg>
                            Send Emails Directly ({selected.length})
                          </button>

                          <button
                            className="button-small"
                            onClick={handleDeselectAll}
                            disabled={isGeneratingEmails || isSendingEmails}
                            style={{
                              padding: '8px 16px',
                              background: '#ff4d4f',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor:
                                isGeneratingEmails || isSendingEmails
                                  ? 'not-allowed'
                                  : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                            Clear Selection
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div
              style={{
                background: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}
            >
              <p>Please select a date from the navigation.</p>
            </div>
          )}
        </div>
        {showEmailConfirmModal && emailToConfirm && (
          <div className="popup-overlay">
            <div className="popup-content">
              <h3>
                Confirm Email
              </h3>
              <p>
                Are you sure you want to set the email for <strong>{emailToConfirm.agentName}</strong> to:
                <br />
                <strong>{emailToConfirm.newEmail}</strong>?
              </p>
              <div className="popup-actions">
                <button className="button-accept" onClick={handleConfirmEmailUpdate}>
                  Yes, Confirm
                </button>
                <button
                  className="button-cancel"
                  onClick={() => {
                    setEmailToConfirm(null);
                    setShowEmailConfirmModal(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showSendConfirmModal && (
          <div className="popup-overlay">
            <div className="popup-content">
              <h3>Confirm Send</h3>
              <p>
                Are you sure you want to send emails directly to <strong>{selected.length}</strong> recipient(s)?<br />
              <span>This cannot be undone.</span>
              </p>
              <div className="popup-actions">
                <button className="button-accept" onClick={confirmSendEmailsNow}>
                  Yes, Confirm
                </button>
                <button
                  className="button-cancel"
                  onClick={() => setShowSendConfirmModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}  
      </div>
      <div style={{ height: '60px' }} />

    </div>
  );
}

export default TableDeclarations;